import type { Aggregate } from '@cognite/sdk';

/**
 * Curated subset of CDF datapoint aggregates exposed end-to-end:
 *
 * - **Server-side** via {@link FetchTimeseriesDatapointsTool} and the
 *   prefetch path in `engine/timeseries-prefetch.ts` (`weekly(rateRef,
 *   {aggregate: 'min'})` etc.) — passed straight through to CDF.
 * - **Client-side** via `clientSideBucket` in
 *   `engine/timeseries-prefetch.ts` for nested forms like
 *   `weekly(datapoints(ref), {aggregate: 'min'})` where the bucketing
 *   happens after raw datapoints have already been fetched.
 *
 * CDF supports more aggregates (`interpolation`, `stepInterpolation`,
 * `totalVariation`, …) but they have no client-side reducer here and the
 * extra surface complicates LLM prompting. Add a value here only after
 * adding a matching reducer in {@link AGGREGATORS}; the `satisfies` clause
 * on `AGGREGATORS` enforces the pairing at the type level.
 */
export const AGGREGATES = ['average', 'min', 'max', 'sum', 'count'] as const satisfies [Aggregate, ...Aggregate[]];

export type SupportedAggregate = (typeof AGGREGATES)[number];

/**
 * Reducer per aggregate, used by `clientSideBucket` to fold a bucket's
 * numeric values into a single output value. Keyed by {@link AGGREGATES}
 * via `satisfies` so adding or removing an aggregate is type-checked at
 * the boundary — no silent drift between the LLM-facing schema and the
 * runtime dispatcher.
 *
 * Reducers are only invoked on non-empty arrays — callers must drop
 * buckets with no contributing datapoints before reducing. `count` is
 * trivially correct on empty input but the other reducers (`average`,
 * `min`, `max`) are not, so the contract is uniform across the map.
 */
export const AGGREGATORS = {
  average: (xs) => xs.reduce((a, b) => a + b, 0) / xs.length,
  min: (xs) => Math.min(...xs),
  max: (xs) => Math.max(...xs),
  sum: (xs) => xs.reduce((a, b) => a + b, 0),
  count: (xs) => xs.length,
} as const satisfies Record<SupportedAggregate, (xs: readonly number[]) => number>;
